import React from 'react';
import {
  Controller,
  FieldValues,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';

type ControllerWrapperProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  'control'
> & {
  component: React.ElementType;
  needsBlurWrapper?: boolean;
  tabIndex?: number;
  [key: string]: any;
};

/**
 * 어떤 UI 컴포넌트든 react-hook-form과 연결해주는 범용 Wrapper 컴포넌트
 */
const ControllerWrapper = <T extends FieldValues>({
  name,
  rules,
  component: Component,
  needsBlurWrapper = false,
  tabIndex,
  ...rest
}: ControllerWrapperProps<T>) => {
  const {control} = useFormContext<T>();

  const isClearButton = (node: Node | null) => {
    if (!(node instanceof Element)) {
      return false;
    }
    return !!node.closest('[class*="clearButton"]');
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({field}) => {
        const {ref, ...fieldWithoutRef} = field;

        if (needsBlurWrapper) {
          return (
            <div
              onMouseDownCapture={(e) => {
                if (isClearButton(e.target as Node)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => {
                const next = e.relatedTarget as Node | null;

                if (isClearButton(next)) {
                  return;
                }
                if (!next || !e.currentTarget.contains(next)) {
                  field.onBlur();
                }
              }}
              tabIndex={tabIndex}>
              <Component {...fieldWithoutRef} {...rest} />
            </div>
          );
        }

        return <Component {...field} {...rest} />;
      }}
    />
  );
};

export default ControllerWrapper;
